import { useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Bell,
  Briefcase,
  RefreshCw,
  TrendingUp,
  Wallet,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/ErrorState";
import Loader from "@/components/loader/Loader";
import { useAppSelector } from "@/store/hooks";
import {
  useMyWalletAndAssets,
  useProfile,
  useUserReputationScore,
} from "@/hooks";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { normalizeCurrency } from "@/utils";
import { formatCurrency } from "@/utils/formatCurrency";

const mockUserAnalytics = {
  user: {
    role: "USER",
    status: "ACTIVE",
    kycLevel: 2,
    kycStatus: "UNDER_REVIEW",
    advertiserTier: "VERIFIED",
    reputationScore: 78,
    completedTrades: 42,
    disputeRate: 3.2,
  },

  wallet: {
    balance: 12450.32,
    inflow: 40200.12,
    outflow: 27749.8,
    reserved: 1250.0,
    feeRevenue: 340.22,
  },

  trading: {
    totalVolume: 98500,
    completedTrades: 42,
    pendingTrades: 3,
    disputes: 2,
    escrowLocked: 6,
  },

  security: {
    activeSessions: 2,
    devices: 3,
    failedLogins: 4,
    loginRiskScore: 22,
  },

  support: {
    openTickets: 1,
    resolvedTickets: 8,
    totalTickets: 9,
  },

  referrals: {
    totalReferrals: 12,
    rewardsEarned: 48.5,
  },

  activity: {
    swaps: 5,
    adsCreated: 3,
    notifications: 21,
  },
};

const tokenColors: Record<string, string> = {
  BTC: "#F7931A",
  ETH: "#627EEA",
  tUSD: "#26A17B",
  tNGN: "#F3BA2F",
  SOL: "#9945FF",
  XRP: "#23292F",
  ADA: "#0033AD",
  DOGE: "#C2A633",
  MATIC: "#8247E5",
  TRX: "#EF0027",
  LTC: "#345D9D",
  DOT: "#E6007A",
  AVAX: "#E84142",
  default: "#8884D8",
};

function StatCard({
  title,
  value,
  icon: Icon,
  description,
}: {
  title: string;
  value: string | number;
  icon: any;
  description?: string;
}) {
  return (
    <Card className="rounded-2xl border bg-background shadow-sm">
      <CardContent className="p-4 flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className="text-xl font-semibold tracking-tight">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
        </div>

        <div className="p-2 rounded-xl bg-muted">
          <Icon className="w-4 h-4 text-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const data = useMemo(() => mockUserAnalytics, []);
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const { data: userScore } = useUserReputationScore(userId);
  const { data: wallet } = useMyWalletAndAssets(userId);

  console.log("USER SCORE", userScore);
  console.log("MY WALLET", wallet);

  const {
    data: profile,
    isLoading,
    isError,
    refetch,
  } = useProfile(userId as string);

  console.log("PROFILE", profile);
  const {
    data: myWallet,
    isLoading: isLoadingWallet,
    isError: isErrorWallet,
    refetch: refetchWallet,
  } = useMyWalletAndAssets(userId!);

  const portfolioHistory = myWallet?.analytics?.portfolioHistory || [];

  const assetAllocation = myWallet?.analytics?.assetAllocation || [];

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch();
    refetchWallet();
    setTimeout(() => {
      setIsRefreshing(false);
      toast.success("Analytics refreshed", {
        description: "Your analytics data has been updated.",
      });
    }, 1000);
  };

  if (isLoading || isLoadingWallet) {
    return <Loader type="bounce" />;
  }

  if (isError || isErrorWallet) {
    return (
      <ErrorState
        onRetry={() => {
          refetch();
          refetchWallet();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background px-6 py-6 space-y-6">
      {/* ================= KPI GRID ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Wallet Balance"
          value={`${myWallet?.totalBalanceCurrency}  ${formatCurrency(myWallet?.totalBalance ?? 0)}`}
          icon={Wallet}
          description="Available funds"
        />
        <StatCard
          title="Trading Volume"
          value={`$${userScore?.tradeVolume.toLocaleString()}`}
          icon={TrendingUp}
          description="Lifetime activity"
        />
        <StatCard
          title="Reputation Score"
          value={userScore?.reputationScore ?? 0}
          icon={BadgeCheck}
          description="Trust index"
        />
        <StatCard
          title="Dispute Rate"
          value={`${userScore?.disputeRate}%`}
          icon={AlertTriangle}
          description="Risk indicator"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Portfolio Chart */}
        <Card className="">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Portfolio Performance</CardTitle>
              <CardDescription>7-day value history</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
              >
                <RefreshCw
                  className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")}
                />
                Refresh
              </Button>
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-violet-500/10"
              >
                7D
              </Badge>
              <Badge variant="secondary" className="text-xs cursor-pointer">
                30D
              </Badge>
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-violet-500/10"
              >
                90D
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={portfolioHistory}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.2}
                />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickFormatter={(v) => `$${v / 1000}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [
                    `${myWallet?.totalBalanceCurrency}  ${value.toLocaleString()}`,
                    "Portfolio Value",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#8b5cf6"
                  fillOpacity={1}
                  fill="url(#colorValue)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Asset Allocation */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-all"
          onClick={() => navigate("/dashboard/wallet")}
        >
          <CardHeader>
            <CardTitle className="text-lg">Asset Allocation</CardTitle>
            <CardDescription>By portfolio value</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={assetAllocation}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {assetAllocation.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={tokenColors[entry.name] || tokenColors.default}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number, name: string, props: any) => [
                    `${value}% (${myWallet?.totalBalanceCurrency}  ${props.payload.amount.toLocaleString()})`,
                    normalizeCurrency(props.payload.name),
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-4">
              {assetAllocation.map((asset: any) => (
                <div
                  key={asset.name}
                  className="flex items-center justify-between cursor-pointer hover:bg-muted/50 p-1 rounded"
                  onClick={() => navigate("/dashboard/wallet")}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{
                        backgroundColor:
                          tokenColors[asset.name] || tokenColors.default,
                      }}
                    />
                    <span className="text-sm">
                      {normalizeCurrency(asset.name)}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium">{asset.value}%</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {myWallet?.totalBalanceCurrency}{" "}
                      {asset.amount.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ================= ACTIVITY ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* <StatCard title="Swaps" value={data.activity.swaps} icon={RefreshCw} /> */}
        <StatCard
          title="Ads Created"
          value={profile?.advertisements?.length ?? 0}
          icon={Briefcase}
        />
        <StatCard
          title="Notifications"
          value={profile?.notifications.length ?? 0}
          icon={Bell}
        />
      </div>

      {/* ================= FINANCIAL + TRADING ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border bg-background shadow-sm">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-4 justify-between">
              <span>Wallet Flow</span>
              <span> {wallet?.totalInflowCurrency} </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Inflows" value={formatCurrency(wallet?.totalInflow)} />
            <Row
              label="Outflows"
              value={formatCurrency(wallet?.totalOutflow)}
            />
            <Row
              label="Reserved"
              value={formatCurrency(wallet?.totalLockedBalance)}
            />
            {/* <Row
              label="Fee Revenue"
              value={`$${data.wallet.feeRevenue.toLocaleString()}`}
              strong
            /> */}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-background shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Trading Health</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Completed Trades" value={userScore?.completedTrades} />
            <Row label="Pending Trades" value={userScore?.activeTradesCount} />
            {/* <Row label="Escrow Locked" value={data.trading.escrowLocked} /> */}
            <Row label="Disputes" value={userScore?.disputesOpened} danger />
          </CardContent>
        </Card>
      </div>

      {/* ================= SECURITY ================= */}
      {/* <Card className="rounded-2xl border bg-background shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Security Overview</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <MiniStat label="Sessions" value={data.security.activeSessions} />
            <MiniStat label="Devices" value={data.security.devices} />
            <MiniStat
              label="Failed Logins"
              value={data.security.failedLogins}
            />
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Risk Score</span>
              <span className="font-medium">
                {data.security.loginRiskScore}/100 ({riskLevel})
              </span>
            </div>
            <Progress value={data.security.loginRiskScore} />
          </div>
        </CardContent>
      </Card> */}

      {/* ================= SUPPORT + REFERRALS ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border bg-background shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Support Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Total Tickets" value={profile?.tickets?.length} />
            <Row label="Open Tickets" value={profile?.tickets?.length} />
            <Row label="Resolved" value={profile?.tickets?.length} />
          </CardContent>
        </Card>

        <Card className="rounded-2xl border bg-background shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Referrals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row
              label="Total Referrals"
              value={profile?.referralRewardsReceived ?? 0}
            />
            <Row
              label="Rewards Earned"
              value={`$${profile?.referralRewardsEarned ?? 0}`}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * ============================
 * SMALL ROW COMPONENT
 * ============================
 */
function Row({
  label,
  value,
  strong,
  danger,
}: {
  label: string;
  value: any;
  strong?: boolean;
  danger?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span
        className={`font-medium ${
          danger ? "text-red-500" : strong ? "text-foreground" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * ============================
 * MINI STAT
 * ============================
 */
function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="p-3 rounded-xl border bg-muted/40">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
